// ONE CONVENTION FOR WHICH WAY THINGS FACE (Khari, 2026-10-30: "objects and
// people are often facing reverse of their intended positions … This is a
// problem with direction across the app. We will need a way to define a target
// direction or global spatial understanding, so we don't get objects created or
// spawned with reverse orientation.").
//
// THE RULE, everywhere in the World:
//   · a thing's FRONT is its local +z (a person's face, a bench's seat side, a
//     sign's text, a prop's working side);
//   · rotation.y = θ turns that front to world (sin θ, cos θ);
//   · so the turn that makes something face a point is yawToward(from, to).
// A model built the other way round (its front on −z) is corrected ONCE, where
// it is built or loaded — never by adding Math.PI where it is placed. That
// half turn sprinkled at call sites is exactly how the plaza's benches ended up
// with their backs to the stage.

/** The rotation.y that turns a thing at `from` [x, z] to face `to` [x, z]. */
export function yawToward(from, to) {
  return Math.atan2(to[0] - from[0], to[1] - from[1]);
}

/** Where a thing at `at` facing `yaw` is looking, `dist` ahead: [x, z]. */
export function ahead(at, yaw, dist = 1) {
  return [at[0] + Math.sin(yaw) * dist, at[1] + Math.cos(yaw) * dist];
}

/** [x, z] turned by θ about the vertical, the same turn rotation.y = θ applies. */
export function turnXZ(v, th) {
  if (!th) return v;
  const c = Math.cos(th); const s = Math.sin(th);
  return [v[0] * c + v[1] * s, -v[0] * s + v[1] * c];
}

// ── WHAT A MARK FACES, NOT AN ANGLE (2026-10-31) ─────────────────────────────
// A stored angle is a guess about the convention made by whoever typed it, and
// the half turn it was off by is invisible until somebody stands there. A
// TARGET cannot be off by a half turn. So a room's mark may say what it faces
// instead of storing rotY:
//
//   face: [x, z]  or  [x, y, z]   a point, in the room's own space
//   face: 'name'                  another mark of this room (stage, storyMarks,
//                                 booth), or a table id (its centre)
//
// Resolved into rotY once, where rooms are defined (rooms.js, venueTypes
// instanceRoom), so everything that reads `mark.rotY` is unchanged. A target
// that names nothing keeps the mark's own rotY, and says so.

const isMark = (v) => !!v && typeof v === 'object' && Array.isArray(v.pos);
const flat = (p) => (p.length === 2 ? p : [p[0], p[2]]);

/** Resolve every `face` on a room's marks into `rotY`, in place. Returns the room. */
export function faceMarks(room, label = '') {
  if (!room) return room;
  const named = new Map();
  const marks = [];
  const take = (name, m) => {
    if (!isMark(m)) return;
    if (name) named.set(name, m.pos);
    marks.push(m);
  };
  for (const [k, v] of Object.entries(room.stage || {})) {
    if (isMark(v)) take(k, v);
    else if (v && typeof v === 'object') for (const [n, m] of Object.entries(v)) take(n, m);
  }
  for (const [n, m] of Object.entries(room.storyMarks || {})) take(n, m);
  take('booth', room.booth);
  for (const t of room.tables || []) {
    if (t.cups?.length) {
      const n = t.cups.length;
      named.set(t.id, [t.cups.reduce((a, c) => a + c[0], 0) / n, t.cups.reduce((a, c) => a + c[2], 0) / n]);
    }
    if (isMark(t.serve)) marks.push(t.serve);
  }
  for (const m of marks) {
    if (m.face == null) continue;
    const to = typeof m.face === 'string' ? named.get(m.face) : m.face;
    if (!Array.isArray(to)) { console.warn(`[facing] ${label}: nothing called "${m.face}" to face; keeping rotY ${m.rotY ?? 0}`); continue; }
    const a = flat(m.pos); const b = flat(to);
    if (Math.hypot(b[0] - a[0], b[1] - a[1]) < 1e-6) continue;
    m.rotY = yawToward(a, b);
  }
  return room;
}
